import React from 'react';
import api from 'controllers/raymanAPI';
import Singletons from 'controllers/Singletons';

function map(item)
{
    return {...item, id: item.timeRecordID}
}

async function getData(taskID, stepIndex)
{
    let indexed = {};

    let response = await api.procedures.tasks.reports.timers.getAll(taskID, stepIndex);
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.timeRecordID] = item
    });
    
    return {array: data, indexed: indexed};
}

const Context = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        create: (data) => {},
        update: (timeRecordID, data) => {},
        delete: (timeRecordID) => {},
    }
});

function CtxProvider({taskID, stepIndex, ...props})
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(() => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        getData(taskID, stepIndex).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [taskID, stepIndex]);

    const internalRefresh = React.useCallback(() => {
        getData(taskID, stepIndex).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [taskID, stepIndex]);

    const internalUpdate = React.useCallback((timeRecordID, body) => {
        let tmp = {...data};
        // console.log(tmp.indexed, timeRecordID, body);
        if(body.order && body.order !== tmp.indexed[timeRecordID].order) internalRefresh();
        else {
            Object.keys(body).forEach((key) => {
                tmp.indexed[timeRecordID][key] = body[key];
            })
            setData(tmp);
        }
    }, [data, internalRefresh]);

    const internalAdd = React.useCallback(async (timeRecordID, item) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        if(data.indexed[timeRecordID] !== undefined) return internalUpdate(timeRecordID, map(item));
        
        let tmp = {...data};
        item = map(item);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];
        setData(tmp);
    }, [internalUpdate, taskID, stepIndex, data]);

    
    const internalDelete = React.useCallback((timeRecordID) => {
        let tmp = {...data};
        let item = tmp.indexed[timeRecordID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);

        delete tmp.indexed[timeRecordID];
        setData(tmp);
    }, [data]);    


    const create = React.useCallback(async (data) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.timers.post(taskID, stepIndex, data);
        if(result.ok)
        {
            let report = await result.json();
            await internalAdd(report.timeRecordID, report);
            return report;
        }
    }, [taskID, stepIndex, internalAdd]);

    const update = React.useCallback(async (timeRecordID, data) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.timers.updateSingle(taskID, stepIndex, timeRecordID, data);
        if(result.ok) internalUpdate(timeRecordID, map(await result.json()));

    }, [taskID, stepIndex, internalUpdate]);

    const remove = React.useCallback(async (timeRecordID) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.timers.deleteSingle(taskID, stepIndex, timeRecordID);
        if(result.ok) internalDelete(timeRecordID);
    }, [taskID, stepIndex, internalDelete]);

    React.useEffect( function handleNotifications() {

        function sanitizeMessage(message)
        {
            if(typeof message.taskID === "string") message.taskID = parseInt(message.taskID);
            if(typeof message.stepIndex === "string") message.stepIndex = parseInt(message.stepIndex);
            if(typeof message.timeRecordID === "string") message.timeRecordID = parseInt(message.timeRecordID);
            return message;
        }
        

        const handleCreate = (message) => { message = sanitizeMessage(message); if(message.taskID === taskID && message.stepIndex === stepIndex) internalAdd(message.timeRecordID, message) };
        const handleUpdate = (message) => { message = sanitizeMessage(message); if(message.taskID === taskID && message.stepIndex === stepIndex) internalUpdate(message.timeRecordID, map(message)) };
        const handleDelete = (message) => { message = sanitizeMessage(message); if(message.taskID === taskID && message.stepIndex === stepIndex) internalDelete(message.timeRecordID) };

        Singletons.NotificationsSocket.on('procedures.tasks.reports.timers.created', handleCreate);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.timers.updated', handleUpdate);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.timers.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket.off('procedures.tasks.reports.timers.created', handleCreate);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.timers.updated', handleUpdate);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.timers.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete, stepIndex, taskID]);

    return <Context.Provider value={{loading: loading, data: {...data}, funcs: {create: create, update: update, delete: remove}}} >
        {props.children}
        </Context.Provider>
}

export {Context as TimersCtx, CtxProvider as TimersCtxProvider};